import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "passwordField",
    "confirmationField",
    "nextButton",
    "passwordError",
    "confirmationError",
    "keyField"
  ]

  static values = { minLength: Number }

  // ----- lifecycle ----------------------------------------------------------

  initialize() {
    this._keypairGenerationInProgress = false   // blocks overlapping async runs
    this._keypairGenerated = false              // allow only one successful run while on step 1
    this._stepObserver = null                   // watches visibility of step 1 to reset flags
    this.keyFieldObserver = null                // watches key field type change to "text"
  }

  connect() {
    // set up key generator
    window.keygen ||= new KeyGen(this._minLen())

    // observe key field becoming visible (type="text") to enable confirm button
    if (this.hasKeyFieldTarget) {
      this.keyFieldObserver = new MutationObserver(() => {
        const inputType = this.keyFieldTarget.getAttribute("type")
        if (inputType === "text") {
          this.keyFieldObserver?.disconnect()
          this.keyFieldObserver = null
        }
      })
      this.keyFieldObserver.observe(this.keyFieldTarget, {
        attributes: true,
        attributeFilter: ["type"]
      })
    }

    // live validation
    if (this.hasPasswordFieldTarget) {
      this.passwordFieldTarget.addEventListener("input", () => this.validateFields())
    }
    if (this.hasConfirmationFieldTarget) {
      this.confirmationFieldTarget.addEventListener("input", () => this.validateFields())
    }

    // reset guards whenever step 1 becomes visible again
    this.observePasswordStepVisibility()

    // Also listen for multistep navigation events to reset state
    this.element.addEventListener("multistep:step-changed", (event) => {
      const currentStep = event.detail?.currentStep
      if (currentStep && this.hasPasswordFieldTarget && currentStep.contains(this.passwordFieldTarget)) {
        this._resetGenerationState()
      }
    })
  }

  disconnect() {
    this.keyFieldObserver?.disconnect()
    this.keyFieldObserver = null
    this._stepObserver?.disconnect()
    this._stepObserver = null
  }

  // ----- actions / handlers -------------------------------------------------

  // Bind via data-action (e.g., keydown.enter->encryption-flow#handleKeyDown)
  handleKeyDown(event) {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, "multistep")
    if (!multistep) return

    const currentStep = multistep.stepTargets[multistep.currentIndex]
    const inPasswordStep =
      this.hasPasswordFieldTarget &&
      this.hasConfirmationFieldTarget &&
      currentStep.contains(this.passwordFieldTarget) &&
      currentStep.contains(this.confirmationFieldTarget)

    event.preventDefault()

    if (inPasswordStep) {
      this.handleKeyStep(event)
    }
  }

  async handleKeyStep(event) {
    // block key auto-repeat and duplicate/in-flight calls
    if (event?.repeat) return
    event?.preventDefault()
    if (this._keypairGenerationInProgress || this._keypairGenerated) return

    this._keypairGenerationInProgress = true
    try {
      window.keygen ||= new KeyGen(this._minLen())

      const password = this.hasPasswordFieldTarget ? this.passwordFieldTarget.value : ""
      const confirmation = this.hasConfirmationFieldTarget ? this.confirmationFieldTarget.value : ""
      const minLength = this._minLen()

      if (!this.isValid(password, confirmation, minLength)) {
        this.validateFields()
        return
      }

      const isLeaked = await this.checkHIBP(password)
      if (isLeaked) {
        alert("This password has been found in a data breach. Please choose another.")
        if (this.hasPasswordFieldTarget) this.passwordFieldTarget.value = ""
        if (this.hasConfirmationFieldTarget) this.confirmationFieldTarget.value = ""
        this.validateFields()
        return
      }

      if (password) {
        if (this.hasKeyFieldTarget) this.keyFieldTarget.value = password
      }

      await window.keygen.generateKeyPair()
      window.keygen.fillForm()

      this.dispatch("ready")
      this.next()

      // success: allow only once while on step 1; resets if user navigates back
      this._keypairGenerated = true
    } catch (e) {
      console.error("encryption-flow#handleKeyStep error", e)
    } finally {
      this._keypairGenerationInProgress = false
    }
  }

  handleDownloadAndComplete(event) {
    event?.preventDefault()

    // First, download the key (same logic as handleDownloadKey)
    const password =
      document.querySelector("#crypt-visible-key")?.value ||
      (this.hasKeyFieldTarget ? this.keyFieldTarget.value : "")

    if (!password) {
      alert("Encryption key not found.")
      return
    }

    const blob = new Blob([password], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "encryption-key.txt"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Then, complete the setup by submitting the form
    this.element.requestSubmit()
  }

  next() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, "multistep")
    multistep?.next?.(new Event("next"))
    requestAnimationFrame(() => {
      this.element.scrollIntoView({ block: "start" })
    })
  }

  goBack() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, "multistep")
    if (multistep) {
      // Use the original multistep back navigation
      multistep.back?.(new Event("back"))

      // Also reset generation state immediately since we're going back
      this._resetGenerationState()
    }
  }

  // ----- validation ---------------------------------------------------------

  validateFields() {
    if (!(this.hasPasswordFieldTarget && this.hasConfirmationFieldTarget)) return

    const password = this.passwordFieldTarget.value.trim()
    const confirmation = this.confirmationFieldTarget.value.trim()
    const min = this._minLen()
    let valid = true

    if (!password || password.length < min) {
      if (this.hasPasswordErrorTarget) {
        this.passwordErrorTarget.textContent = `Encryption Key Password must be at least ${min} characters.`
        this.passwordErrorTarget.classList.remove("hidden")
      }
      valid = false
    } else if (this.hasPasswordErrorTarget) {
      this.passwordErrorTarget.textContent = ""
      this.passwordErrorTarget.classList.add("hidden")
    }

    if (!confirmation) {
      if (this.hasConfirmationErrorTarget) {
        this.confirmationErrorTarget.textContent = ""
        this.confirmationErrorTarget.classList.add("hidden")
      }
      valid = false
    } else if (password !== confirmation) {
      if (this.hasConfirmationErrorTarget) {
        this.confirmationErrorTarget.textContent = "Encryption Key Passwords do not match."
        this.confirmationErrorTarget.classList.remove("hidden")
      }
      valid = false
    } else if (this.hasConfirmationErrorTarget) {
      this.confirmationErrorTarget.textContent = ""
      this.confirmationErrorTarget.classList.add("hidden")
    }

    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !valid
  }

  isValid(password, confirmation, minLength) {
    return password.length >= minLength && password === confirmation
  }

  async checkHIBP(password) {
    // skip external call in test env; avoid ReferenceError if railsEnv is unset
    if (typeof window.railsEnv === "string" && window.railsEnv === "test") return false

    const sha1 = async (str) => {
      const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(str))
      return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase()
    }

    const hash = await sha1(password)
    const prefix = hash.substring(0, 5)
    const suffix = hash.substring(5)
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`)
    const text = await res.text()
    return text.includes(suffix)
  }

  // ----- misc helpers -------------------------------------------------------

  observePasswordStepVisibility() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, "multistep")
    if (!multistep) return

    const stepEl = multistep.stepTargets.find((step) =>
      this.hasPasswordFieldTarget ? step.contains(this.passwordFieldTarget) : false
    )
    if (!stepEl) return

    const onChange = () => {
      if (this._isVisible(stepEl)) this._resetGenerationState()
    }

    this._stepObserver?.disconnect()
    this._stepObserver = new MutationObserver(onChange)
    this._stepObserver.observe(stepEl, { attributes: true, attributeFilter: ["hidden", "class", "style"] })
    onChange() // initialize
  }

  _isVisible(el) {
    return el && !el.hidden && !el.classList.contains("hidden") && el.offsetParent !== null
  }

  _resetGenerationState() {
    this._keypairGenerationInProgress = false
    this._keypairGenerated = false
    // Re-validate fields to update button state when returning to step 1
    this.validateFields()
  }

  _minLen() {
    return this.hasMinLengthValue ? this.minLengthValue : 12
  }
}
